<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { projects } from '../utils/projectData';
import Navigation from '../components/Navigation.vue';
import router from '../router';

const route = useRoute();
const projectId = parseInt(route.params.id, 10);
const project = computed(() => projects.value.find(p => p.id === projectId));

const backToProjectPage = () => {
  router.push({ path: '/', hash: '#projects' }).catch(err => {
    console.error('Navigation error:', err);
  });
};
</script>

<template>
  <Navigation />
  <div class="content-container">
    <button class="back-btn" @click="backToProjectPage">
      <span class="back-arrow">←</span> Back to all projects
    </button>

    <div class="project-detail-wrapper" v-if="project">
      <div class="project-detail">
        <h1>{{ project.title }}</h1>
        <p>{{ project.description }}</p>
        <p>{{ project.subdescription }}</p>

        <div class="stack-section">
          <h2>Stack</h2>
          <ul>
            <li v-for="(stackItem, index) in project.stack" :key="index">{{ stackItem }}</li>
          </ul>
        </div>

        <a
          v-if="project.url"
          :href="project.url"
          target="_blank"
          rel="noopener noreferrer"
          class="project-link"
        >
          View Project →
        </a>
      </div>

      <div class="project-images">
        <div v-if="project.images && project.images.length" class="image-grid">
          <img
            v-for="(image, index) in project.images"
            :key="index"
            :src="image"
            :alt="`Image ${index + 1}`"
          />
        </div>
      </div>
    </div>

    <div v-else class="not-found">
      <p>Project not found.</p>
    </div>
  </div>
</template>

<style scoped>
.content-container {
  --ink: #ffffff;
  --muted: rgba(255, 255, 255, 0.6);
  --line: rgba(255, 255, 255, 0.12);
  --accent-1: #667eea;
  --accent-2: #764ba2;

  margin-top: 70px;
  padding: 48px 40px 80px;
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
  color: var(--ink);
}

/* Back button */
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 32px;
  padding: 0;
  border: none;
  background: none;
  color: var(--muted);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s;
}
.back-btn:hover { color: var(--ink); }
.back-arrow { transition: transform 0.2s; }
.back-btn:hover .back-arrow { transform: translateX(-3px); }

/* Layout */
.project-detail-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 48px;
}

.project-detail {
  flex: 1 1 380px;
  max-width: 520px;
}

.project-detail h1 {
  font-size: clamp(1.9rem, 3vw, 2.4rem);
  font-weight: 800;
  letter-spacing: -1px;
  margin-bottom: 18px;
  background: linear-gradient(-45deg, var(--accent-1), var(--accent-2), #a8c0ff);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.project-detail p {
  margin-bottom: 16px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.85);
  font-size: 16px;
}

.stack-section {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid var(--line);
}

.stack-section h2 {
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 12px;
  font-weight: 600;
}

.stack-section ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stack-section li {
  font-size: 13px;
  padding: 5px 14px;
  border: 1px solid var(--line);
  border-radius: 20px;
  color: rgba(255, 255, 255, 0.85);
}

.project-link {
  display: inline-flex;
  align-items: center;
  margin-top: 28px;
  padding: 12px 22px;
  background: linear-gradient(90deg, var(--accent-1), var(--accent-2));
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  border-radius: 8px;
  transition: opacity 0.2s, transform 0.2s;
}
.project-link:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

/* Images */
.project-images {
  flex: 1 1 420px;
}

.image-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.image-grid img {
  width: calc(33.333% - 8px);
  height: auto;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid var(--line);
  transition: transform 0.3s;
}

.image-grid img:hover {
  transform: scale(1.02);
}

.not-found {
  text-align: center;
  color: var(--muted);
  padding: 80px 0;
}

/* Mobile */
@media (max-width: 768px) {
  .content-container {
    margin-top: 56px;
    padding: 28px 20px 56px;
  }

  .back-btn {
    margin-bottom: 24px;
    font-size: 13px;
  }

  .project-detail-wrapper {
    flex-direction: column;
    gap: 32px;
  }

  .project-detail {
    max-width: 100%;
  }

  .project-detail h1 {
    font-size: 1.7rem;
  }

  .project-detail p {
    font-size: 15px;
    line-height: 1.65;
  }

  .stack-section ul {
    justify-content: flex-start;
  }

  .project-link {
    width: 100%;
    justify-content: center;
  }

  .image-grid {
    flex-direction: column;
    gap: 14px;
  }

  .image-grid img {
    width: 100%;
    border-radius: 12px;
  }
}
</style>
